import { UTCDate } from '@date-fns/utc'
import { format, startOfMonth, subMonths } from 'date-fns'
import { formatUtcMonth } from '@/lib/format'
import type { PeriodOption } from '../types'

// Billing periods are UTC months: the relay closes a month and picks the
// current period on the UTC calendar. Everything here that names a month
// reads the date in UTC, so a browser west of UTC on the last evening of a
// month asks for, and labels, the same month the relay is serving.

/** Format a date as "YYYY-MM" for use as a period key. */
export function periodKey(date: Date): string {
  return format(new UTCDate(date), 'yyyy-MM')
}

/** The current UTC month: the relay judges a credit's month in UTC. */
export function currentUtcMonth() {
  const now = new UTCDate()
  return {
    period: periodKey(now),
    today: format(now, 'yyyy-MM-dd'),
    first: format(startOfMonth(now), 'yyyy-MM-dd'),
  }
}

/** The UTC month a date or timestamp string falls in, as its first instant.
 *  Takes a period key's "YYYY-MM-01" or a relay date such as
 *  `earliest_period_start`, with or without a time. */
export function utcMonthOf(value: string): Date {
  return startOfMonth(new UTCDate(value))
}

/** True when `value` is a well-formed period key: "YYYY-MM" with month 01-12. */
export function isPeriodKey(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
}

/** Resolve the `?month=` search param to a renderable period key.
 *
 *  Falls back to the current month when the param is missing, malformed, or a
 *  future month. A well-formed past month is trusted while the org's ledger
 *  range is still loading (`historyKnown` false) so a deep link doesn't flash
 *  current-month data first; once the range is known, a month outside
 *  `options` (before the org's earliest ledger entry) also falls back.
 *
 *  Period keys are zero-padded "YYYY-MM", so string comparison orders them
 *  chronologically.
 */
export function resolvePeriodParam(
  raw: string | null,
  options: PeriodOption[],
  historyKnown: boolean,
  now: Date = new Date()
): string {
  const current = periodKey(now)
  if (raw === null || !isPeriodKey(raw) || raw >= current) return current
  if (!historyKnown) return raw
  return options.some((o) => o.value === raw) ? raw : current
}

/** True when the URL's `?month=` param should be removed: it's either invalid
 *  (it isn't the period the page resolved to render — garbage, future, or
 *  before the org's history) or redundant (it names the current month, whose
 *  canonical URL carries no param). A trusted past-month deep link — where
 *  `raw` equals the resolved period and isn't the current month — is kept.
 */
export function shouldStripMonthParam(
  raw: string | null,
  resolved: string,
  current: string
): boolean {
  if (raw === null) return false
  return raw !== resolved || raw === current
}

function makeOption(start: Date, isCurrent: boolean): PeriodOption {
  return {
    value: periodKey(start),
    label: `${formatUtcMonth(start)}${isCurrent ? ' (current)' : ''}`,
    isCurrent,
  }
}

/** Build a PeriodOption for a well-formed period key that isn't (yet) in the
 *  dropdown — a trusted deep-linked past month while the org's ledger range
 *  is still loading. Never marked current: `buildPeriodOptions` always emits
 *  the current month, so a key missing from options is a past month.
 */
export function periodOptionForKey(value: string): PeriodOption {
  return makeOption(utcMonthOf(`${value}-01`), false)
}

/** Build the dropdown options spanning current month back to `earliestMonth`.
 *
 *  `earliestMonth` should be the first day of the earliest month the org has
 *  ledger data for (from the relay's `earliest_period_start`). When null
 *  (org has no ledger entries yet), only the current month is offered.
 */
export function buildPeriodOptions(
  now: Date = new Date(),
  earliestMonth: Date | null = null
): PeriodOption[] {
  const current = startOfMonth(new UTCDate(now))
  // Clamp the floor to `current` so the current month is always emitted
  // even if `earliest_period_start` is somehow ahead of the clock.
  const rawFloor = earliestMonth
    ? startOfMonth(new UTCDate(earliestMonth))
    : current
  const floor = rawFloor > current ? current : rawFloor
  const options: PeriodOption[] = []
  let cursor = current
  while (cursor >= floor) {
    options.push(makeOption(cursor, cursor.getTime() === current.getTime()))
    cursor = subMonths(cursor, 1)
  }
  return options
}

/** The latest `months` UTC months, newest first, the current one included.
 *  Counted back from the UTC month so the span is the same length at every
 *  hour, including the evening a browser west of UTC is still a month behind. */
export function recentPeriodOptions(
  months: number,
  now: Date = new Date()
): PeriodOption[] {
  return buildPeriodOptions(
    now,
    subMonths(startOfMonth(new UTCDate(now)), months - 1)
  )
}

/** Days elapsed in the current UTC calendar month (1 on the 1st, etc.). */
export function daysElapsedInMonth(now: Date = new Date()): number {
  return now.getUTCDate()
}

/** End-of-period date for forecast calculations: today + N days. */
export function projectEndDate(
  daysFromNow: number,
  now: Date = new Date()
): Date {
  const projected = new Date(now)
  projected.setDate(projected.getDate() + Math.floor(daysFromNow))
  return projected
}
